import React, { useState, useContext, useMemo } from "react";
import { Users, Eye, Edit2, Trash2, FileText } from "lucide-react";
import { ThemeContext } from "../../theme";
import { LanguageContext } from "../../LanguageContext";
import { useEmployees } from "../hooks/useEmployees";
import { useEmployeeFilters } from "../hooks/useEmployeeFilters";
import { useEmployeeProfilePics } from "../hooks/useEmployeeProfilePics";
import ReusableDropdown from "../../../components/ReusableDropdown";
import { text } from "../constants/text";
import FiltersPanel from "./FiltersPanel";
/**
 * ✅ EmployeeTableIntegrated - Dark theme employee table
 */
export default function EmployeeTableIntegrated({
  onViewEmployee,
  onEditEmployee,
  onDeleteEmployee,
  onViewDocuments,
}) {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  const { employees: allEmployees, loading, error } = useEmployees();
  const {
    searchTerm,
    setSearchTerm,
    filterDepartment,
    setFilterDepartment,
    filterStatus,
    setFilterStatus,
    filterCompany,
    setFilterCompany,
    showAllEmployees,
  } = useEmployeeFilters();
  const { employeeProfilePics } = useEmployeeProfilePics(allEmployees);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getScreenSize = () => {
    if (typeof window === "undefined") return "md";
    const width = window.innerWidth;
    if (width < 640) return "xs";
    if (width < 768) return "sm";
    if (width < 1024) return "md";
    if (width < 1280) return "lg";
    if (width < 1536) return "xl";
    return "xxl";
  };

  const [screenSize, setScreenSize] = React.useState(getScreenSize());

  React.useEffect(() => {
    const handleResize = () => setScreenSize(getScreenSize());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Define color scheme based on theme
  const style = {
    border:
      theme === "light"
        ? "rgba(26, 26, 26, 0.15)"
        : "rgba(255, 255, 255, 0.15)",
    shadow:
      theme === "light"
        ? "0 1px 3px rgba(0, 0, 0, 0.1)"
        : "0 8px 32px rgba(0, 0, 0, 0.3)",
    bgPrimary: theme === "light" ? "#ffffff" : "rgba(30, 30, 46, 0.8)",
    bgSecondary:
      theme === "light"
        ? "rgba(26, 26, 26, 0.02)"
        : "rgba(255, 255, 255, 0.05)",
    cardBg: theme === "light" ? "#ffffff" : "rgba(10, 10, 161, 0.01)",
    inputBg:
      theme === "light"
        ? "rgba(26, 26, 26, 0.05)"
        : "rgba(255, 255, 255, 0.08)",
    textPrimary: theme === "light" ? "#1a1a1a" : "#ffffff",
    textMuted:
      theme === "light" ? "rgba(26, 26, 26, 0.6)" : "rgba(255, 255, 255, 0.6)",
    textSecondary: theme === "light" ? "#1a1a1a" : "#ffffff",
    accent: "#2e7cba",
    hoverBg:
      theme === "light"
        ? "rgba(59, 130, 246, 0.08)"
        : "rgba(59, 130, 246, 0.15)",
  };

  const filteredEmployees = useMemo(() => {
    const list = Array.isArray(allEmployees) ? allEmployees : [];
    if (showAllEmployees) return list;
    const term = (searchTerm || "").toLowerCase().trim();
    return list.filter((emp) => {
      if (!emp) return false;
      const fullName = (emp.name || "").toLowerCase();
      const arabicName = (emp.arabicName || "").toLowerCase();
      const id = String(emp.id || "").toLowerCase();
      const email = (emp.email || "").toLowerCase();
      if (
        term &&
        !(
          fullName.includes(term) ||
          arabicName.includes(term) ||
          id.includes(term) ||
          email.includes(term)
        )
      )
        return false;
      if (
        filterDepartment &&
        !(emp.department || "")
          .toLowerCase()
          .includes(filterDepartment.toLowerCase())
      )
        return false;
      if (filterStatus && (emp.status || "") !== filterStatus) return false;
      if (filterCompany && (emp.company || "") !== filterCompany) return false;
      return true;
    });
  }, [
    allEmployees,
    searchTerm,
    filterDepartment,
    filterStatus,
    filterCompany,
    showAllEmployees,
  ]);

  const totalPages = Math.ceil((filteredEmployees?.length || 0) / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const displayedEmployees = (filteredEmployees || []).slice(
    startIdx,
    startIdx + itemsPerPage,
  );

  const getUserInitials = (name) => {
    if (!name) return "??";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      Active: {
        bg:
          theme === "dark"
            ? "rgba(16, 185, 129, 0.2)"
            : "rgba(16, 185, 129, 0.1)",
        text: "#10b981",
        label: text[language]?.statusActive || "Active",
      },
      OnLeave: {
        bg:
          theme === "dark"
            ? "rgba(245, 158, 11, 0.2)"
            : "rgba(245, 158, 11, 0.1)",
        text: "#f59e0b",
        label: text[language]?.statusOnLeave || "On Leave",
      },
      Inactive: {
        bg:
          theme === "dark"
            ? "rgba(239, 68, 68, 0.2)"
            : "rgba(239, 68, 68, 0.1)",
        text: "#ef4444",
        label: text[language]?.statusInactive || "Inactive",
      },
    };
    const style = statusStyles[status] || statusStyles.Active;
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 12px",
          borderRadius: "20px",
          fontSize: "12px",
          fontWeight: "600",
          backgroundColor: style.bg,
          color: style.text,
          border: `1px solid ${style.text}`,
        }}
      >
        {style.label}
      </span>
    );
  };

  const styles = {
    outerWrapper: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      marginBottom: "24px",
    },
    filterCard: {
      background: style.cardBg,
      border: `1px solid ${style.border}`,
      borderRadius: "16px",
      overflow: "hidden",
      boxShadow: style.shadow,
      backdropFilter: theme === "dark" ? "blur(10px)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(10px)" : "blur(0px)",
    },
    tableCard: {
      borderRadius: "16px",
      overflow: "hidden",
      width: "100%",
      //background: style.bgPrimary,
      border: `1px solid ${style.border}`,
      boxShadow: style.shadow,
      backdropFilter: theme === "dark" ? "blur(10px)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(10px)" : "blur(0px)",
    },
    header: {
      padding: screenSize === "xs" ? "16px" : "24px",
      borderBottom: `1px solid ${style.border}`,
      background: style.bgSecondary,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
    },
    headerContent: { flex: 1 },
    headerTitle: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontSize: screenSize === "xs" ? "16px" : "20px",
      fontWeight: "700",
      color: style.textPrimary,
      margin: "0 0 8px 0",
    },
    headerSubtitle: {
      fontSize: "13px",
      color: style.textMuted,
      margin: "0",
      marginTop: "8px",
      marginRight: "42px",
    },
    filterBar: {
      padding: screenSize === "xs" ? "12px" : "16px",
      background: style.cardBg,
      display: "grid",
      gridTemplateColumns:
        screenSize === "xs"
          ? "1fr"
          : screenSize === "sm"
            ? "repeat(2, 1fr)"
            : "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "12px",
    },
    filterGroup: { display: "flex", flexDirection: "column", gap: "6px" },
    filterLabel: {
      fontSize: "12px",
      fontWeight: "600",
      color: style.textPrimary,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    filterInput: {
      padding: "10px 12px",
      border: `1px solid ${style.border}`,
      borderRadius: "8px",
      background: style.inputBg,
      color: style.textPrimary,
      fontSize: "14px",
      transition: "all 0.2s",
      outline: "none",
    },
    tableWrapper: { overflowX: "auto", padding: "16px" },
    table: {
      width: "100%",
      borderCollapse: "separate",
      borderSpacing: 0,
      fontSize: screenSize === "xs" ? "12px" : "14px",
      borderRadius: "12px",
      overflow: "hidden",
      border: `1px solid ${style.border}`,
      marginTop:-15,
    },
    tableHead: {
      background:
        theme === "dark"
          ? "linear-gradient(90deg, rgba(66, 66, 66, 0.88) 0%, rgba(66, 66, 66, 0.88) 100%)"
          : "linear-gradient(90deg, rgba(220, 220, 220, 0.95) 0%, rgba(220, 220, 220, 0.95) 100%)", // ← light bg for light mode
      borderBottom:
        theme === "dark"
          ? "2px solid rgba(59, 130, 246, 0.3)"
          : "2px solid rgba(59, 130, 246, 0.2)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
    },
    th: {
      padding: screenSize === "xs" ? "14px 8px" : "18px 12px",
      textAlign: "center",
      fontWeight: "700",
      color: style.textPrimary,
      fontSize: screenSize === "xs" ? "11px" : "13px",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      borderBottom: "none",
      whiteSpace: "nowrap",
      background: "transparent",
    },
    tr: {
      borderBottom: "1px solid " + style.border,
      transition: "background 0.2s ease",
    },
    td: {
      padding: screenSize === "xs" ? "12px 8px" : "16px 12px",
      color: style.textPrimary,
      verticalAlign: "middle",
      textAlign: "center",
    },
    avatar: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: screenSize === "xs" ? "32px" : "40px",
      height: screenSize === "xs" ? "32px" : "40px",
      borderRadius: "50%",
      background: "linear-gradient(135deg, #667eea 0%, #2e7cba 100%)",
      color: "#fff",
      fontWeight: "700",
      fontSize: screenSize === "xs" ? "11px" : "13px",
      marginLeft: "20px",
      flexShrink: 0,
    },
    employeeCell: { display: "flex", alignItems: "center", gap: "8px" },
    employeeName: {
      fontWeight: "600",
      color: style.textPrimary,
      display: "block",
      fontSize: screenSize === "xs" ? "13px" : "14px",
    },
    employeeEmail: {
      fontSize: "12px",
      color: style.textMuted,
      display: "block",
    },
    actionsCell: {
      display: "flex",
      gap: "6px",
      alignItems: "center",
      justifyContent: "center",
    },
    actionBtn: {
      background: "transparent",
      border: `1px solid ${style.border}`,
      borderRadius: "6px",
      padding: "6px 10px",
      cursor: "pointer",
      color: style.textSecondary,
      transition: "all 0.2s ease",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      fontWeight: "500",
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "8px",
      padding: "16px",
      borderTop: `1px solid ${style.border}`,
      background: style.bgSecondary,
    },
    pageBtn: {
      padding: "6px 12px",
      borderRadius: "6px",
      border: `1px solid ${style.border}`,
      background: style.inputBg,
      color: style.textPrimary,
      cursor: "pointer",
      transition: "all 0.2s ease",
      fontSize: "12px",
      fontWeight: "500",
    },
    pageBtnActive: {
      background: style.accent,
      border: `1px solid ${style.accent}`,
      color: "#fff",
      fontWeight: "600",
    },
    emptyState: {
      textAlign: "center",
      padding: "48px 24px",
      color: style.textMuted,
    },
  };

  if (loading)
    return (
      <div style={styles.tableCard}>
        <div style={styles.emptyState}>
          <p
            style={{
              fontSize: "16px",
              fontWeight: "500",
              color: style.textPrimary,
            }}
          >
            {text[language]?.loading || "Loading..."}
          </p>
        </div>
      </div>
    );
  if (error)
    return (
      <div style={styles.tableCard}>
        <div style={styles.emptyState}>
          <p style={{ fontSize: "16px", color: "#ef4444", fontWeight: "500" }}>
            {text[language]?.errorLoadingEmployees || "Error loading employees"}
          </p>
        </div>
      </div>
    );

  return (
    <div style={styles.outerWrapper}>
      {/* Filters Panel */}
      <div style={styles.filterCard}>
        <FiltersPanel
          theme={theme}
          language={language}
          styles={styles}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filterDepartment={filterDepartment}
          setFilterDepartment={setFilterDepartment}
          filterStatus={filterStatus}
          setFilterStatus={setFilterStatus}
          filterCompany={filterCompany}
          setFilterCompany={setFilterCompany}
          departmentsList={Array.isArray(allEmployees) ? Array.from(new Set(allEmployees.map(e => e?.department || "").filter(Boolean))).sort() : []}
          companiesList={Array.isArray(allEmployees) ? Array.from(new Set(allEmployees.map(e => e?.company || e?.workingCompany || e?.clientName || "").filter(Boolean))).sort() : []}
          screenSize={screenSize}
        />
      </div>

      {/* Table Card */}
      <div >
        {/* <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.headerTitle}>
              <Users size={screenSize === "xs" ? 20 : 28} />
              <span>
                {text[language]?.employeeList ||
                  `Employees (${filteredEmployees?.length || 0})`}
              </span>
            </div>
            <p style={styles.headerSubtitle}>
              {text[language]?.employeeListDescription ||
                "Manage and view all employees"}
            </p>
          </div>
        </div> */}
        <div style={styles.tableWrapper}>
        {displayedEmployees.length > 0 ? (
          <table style={styles.table}>
            <thead style={styles.tableHead}>
              <tr>
                <th
                  style={{
                    ...styles.th,
                    textAlign: "left",
                    paddingLeft: "35px",
                  }}
                >
                  {text[language]?.employee || "Employee"}
                </th>
                {screenSize !== "xs" && (
                  <th style={styles.th}>
                    {text[language]?.department || "Department"}
                  </th>
                )}
                {screenSize !== "sm" && (
                  <th style={styles.th}>
                    {text[language]?.joinDate || "Join Daate"}
                  </th>
                )}
                <th style={styles.th}>{text[language]?.Status || "Status"}</th>
                <th style={styles.th}>
                  {text[language]?.actions || "Actions"}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayedEmployees.map((emp, idx) => {
                const profilePic = employeeProfilePics?.[emp.id];
                return (
                  <tr
                    key={emp.id || idx}
                    style={styles.tr}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = style.hoverBg;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <td style={{ ...styles.td, textAlign: "left" }}>
                      <div style={styles.employeeCell}>
                        {profilePic ? (
                          <img
                            src={profilePic}
                            alt={emp.name}
                            style={{
                              ...styles.avatar,
                              objectFit: "cover",
                              background: "transparent",
                            }}
                          />
                        ) : (
                          <div style={styles.avatar}>
                            {getUserInitials(emp.name)}
                          </div>
                        )}
                        <div
                          style={{
                            flex: 1,
                            textAlign: language === "ar" ? "right" : "left",
                            marginLeft: language === "ar" ? "0px" : "10px",
                            marginRight: language === "ar" ? "10px" : "0px",
                            direction: language === "ar" ? "rtl" : "ltr",
                          }}
                        >
                          <div style={{
                            ...styles.employeeName,
                            textAlign: language === "ar" ? "right" : "left",
                          }}>
                            {language === "ar" 
                              ? (emp.arabicName || emp.name || "N/A")
                              : (emp.name || "N/A")}
                          </div>

                          <div style={{
                            ...styles.employeeEmail,
                            textAlign: language === "ar" ? "right" : "left",
                          }}>
                            {emp.email || ""}
                          </div>
                        </div>
                      </div>
                    </td>
                    {screenSize !== "xs" && (
                      <td style={styles.td}>{emp.department || "-"}</td>
                    )}
                    {screenSize !== "sm" && (
                      <td style={styles.td}>
                        {emp.joinDate
                          ? new Date(emp.joinDate).toLocaleDateString()
                          : "-"}
                      </td>
                    )}
                    <td style={styles.td}>{getStatusBadge(emp.status)}</td>
                    <td style={styles.td}>
                      <div style={styles.actionsCell}>
                        <button
                          style={{
                            ...styles.actionBtn,
                            borderColor: "rgba(59, 130, 246, 0.3)",
                            color: "#2e7cba",
                          }}
                        onClick={() => onViewEmployee?.(emp)}
                          title="View"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          style={{
                            ...styles.actionBtn,
                            background: "#2e7cba",
                            borderColor: "#2e7cba",
                            color: "#fff",
                          }}
                          onClick={() => onViewDocuments?.(emp)}
                          title="Documents"
                        >
                          <FileText size={14} />
                        </button>
                        <button
                          style={{
                            ...styles.actionBtn,
                            borderColor: "rgba(34, 197, 94, 0.3)",
                            color: "#22c55e",
                          }}
                          onClick={() => onEditEmployee?.(emp)}
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          style={{
                            ...styles.actionBtn,
                            borderColor: "rgba(239, 68, 68, 0.3)",
                            color: "#ef4444",
                          }}
                          onClick={() => onDeleteEmployee?.(emp.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={styles.emptyState}>
            <p
              style={{
                fontSize: "16px",
                fontWeight: "500",
                color: style.textMuted,
              }}
            >
              {text[language]?.noEmployeesFound || "No employees found"}
            </p>
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div style={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              style={{
                ...styles.pageBtn,
                ...(currentPage === page ? styles.pageBtnActive : {}),
              }}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
        </div>
      )}
      </div>
    </div>
  );
}